// Configuración → APIs: Clientify (migración) y Facebook Lead Ads (webhook + formularios).
// Solo administradores. Los secretos se guardan cifrados y nunca se vuelven a mostrar.
import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Download, Eye, EyeOff, KeyRound, Megaphone, Link2, RefreshCw, Save, Send, Settings2, Trash2 } from "lucide-react";
import { api, ApiError } from "../../lib/api.ts";
import { useMe } from "../../lib/auth.ts";
import { formatDateTime } from "../../lib/format.ts";
import { Badge, Button, Card, Field, Input, Loading, Select } from "../../components/ui.tsx";
import { toast } from "../../components/toast.tsx";

const errText = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback);

export function ApisPanel(): JSX.Element {
  const me = useMe();
  if (!me.data?.admin) return <Card className="p-6"><p className="text-sm text-ink-muted">Solo administradores.</p></Card>;
  return (
    <div className="space-y-4">
      <ClientifyCard />
      <FacebookCard />
    </div>
  );
}

/** Campo de secreto con botón mostrar/ocultar. */
function SecretInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }): JSX.Element {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} autoComplete="off" spellCheck={false} />
      <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary" onClick={() => setShow((v) => !v)} aria-label="Mostrar/ocultar">
        {show ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
      </button>
    </div>
  );
}

// ────────── Clientify ──────────
interface ClientifyCfg { hasKey: boolean; last4: string }

function ClientifyCard(): JSX.Element {
  const qc = useQueryClient();
  const cfg = useQuery<ClientifyCfg>({ queryKey: ["clientify-settings"], queryFn: () => api.get("/settings/clientify") });
  const [apiKey, setApiKey] = useState("");
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["clientify-settings"] }); };

  const save = useMutation({
    mutationFn: () => api.put("/settings/clientify", { apiKey }),
    onSuccess: () => { toast.success("API Key guardada (cifrada)."); setApiKey(""); inval(); },
    onError: (e) => toast.error(errText(e, "No se pudo guardar.")),
  });
  const test = useMutation({
    mutationFn: () => api.post<{ ok: boolean; version?: string; users?: number }>("/settings/clientify/test", {}),
    onSuccess: (r) => toast.success(`Conexión correcta con Clientify (API ${r.version}${r.users !== undefined ? ` · ${r.users} usuarios` : ""}).`),
    onError: (e) => toast.error(errText(e, "No se pudo conectar con Clientify.")),
  });
  const remove = useMutation({
    mutationFn: () => api.del("/settings/clientify"),
    onSuccess: () => { toast.success("API Key eliminada."); inval(); },
    onError: () => toast.error("No se pudo eliminar."),
  });

  if (cfg.isLoading || !cfg.data) return <Card className="p-6"><Loading /></Card>;
  return (
    <Card className="p-6">
      <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
        <KeyRound size={18} strokeWidth={1.75} className="text-primary" /> Clientify · API Key
      </h3>
      <p className="text-sm text-ink-secondary mt-1 mb-5">
        Se usa para extraer los datos de Clientify (contactos, empresas, oportunidades, tareas…) hacia Tiger.
        La clave se guarda cifrada y nunca se vuelve a mostrar.
      </p>
      <div className="mb-4">
        {cfg.data.hasKey
          ? <Badge color="green">Clave guardada · termina en …{cfg.data.last4}</Badge>
          : <Badge color="gray">Sin clave guardada</Badge>}
      </div>
      <Field label={cfg.data.hasKey ? "Reemplazar API Key" : "API Key"}>
        <SecretInput value={apiKey} onChange={setApiKey} placeholder="Pega aquí la API Key de Clientify" />
      </Field>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending || apiKey.trim().length < 20}>
          <Save size={16} strokeWidth={1.75} /> {save.isPending ? "Guardando…" : "Guardar"}
        </Button>
        <Button onClick={() => test.mutate()} disabled={test.isPending || !cfg.data.hasKey}>
          <Send size={16} strokeWidth={1.75} /> {test.isPending ? "Probando…" : "Probar conexión"}
        </Button>
        {cfg.data.hasKey && (
          <Button variant="danger" onClick={() => { if (window.confirm("¿Eliminar la API Key de Clientify?")) remove.mutate(); }} disabled={remove.isPending}>
            <Trash2 size={16} strokeWidth={1.75} /> Eliminar
          </Button>
        )}
      </div>
    </Card>
  );
}

// ────────── Facebook Lead Ads ──────────
interface FbCfg { appId: string; hasAppSecret: boolean; pageId: string; hasPageToken: boolean; verifyToken: string; webhookUrl: string; graphVersion: string }
interface FbFormSettings {
  tags?: string[]; listIds?: string[]; ownerId?: string | null; fieldMap?: Record<string, string>;
  meta?: { status?: string; leadsCount?: number; questions?: Array<{ key: string; label?: string; type?: string }> };
}
interface FbForm { id: string; fbFormId: string; name: string; leadsCount: number; lastLeadAt: string | null; failedLeads: number; settings: FbFormSettings }
interface FbLead { id: string; leadgenId: string; fbFormId: string | null; contactId: string | null; status: string; error: string | null; createdAt: string }
interface Lk { lists: Array<{ id: string; name: string }>; users: Array<{ id: string; name: string }>; customFields: Array<{ key: string; label: string }> }

const copy = (text: string): void => {
  void navigator.clipboard?.writeText(text).then(() => toast.success("Copiado."), () => toast.error("No se pudo copiar."));
};

function FacebookCard(): JSX.Element {
  const qc = useQueryClient();
  const cfg = useQuery<FbCfg>({ queryKey: ["fb-settings"], queryFn: () => api.get("/mkt/facebook/settings") });
  const forms = useQuery<{ forms: FbForm[] }>({ queryKey: ["fb-forms"], queryFn: () => api.get("/mkt/facebook/forms") });
  const [appId, setAppId] = useState<string | null>(null);
  const [pageId, setPageId] = useState<string | null>(null);
  const [appSecret, setAppSecret] = useState("");
  const [pageToken, setPageToken] = useState("");
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["fb-settings"] }); void qc.invalidateQueries({ queryKey: ["fb-forms"] }); };

  const save = useMutation({
    mutationFn: (extra: Record<string, unknown>) => api.put("/mkt/facebook/settings", {
      ...(appId !== null ? { appId: appId.trim() } : {}),
      ...(pageId !== null ? { pageId: pageId.trim() } : {}),
      ...(appSecret.trim() ? { appSecret: appSecret.trim() } : {}),
      ...(pageToken.trim() ? { pageToken: pageToken.trim() } : {}),
      ...(verifyToken !== null && verifyToken.trim() ? { verifyToken: verifyToken.trim() } : {}),
      ...extra,
    }),
    onSuccess: () => { toast.success("Configuración de Facebook guardada."); setAppSecret(""); setPageToken(""); setAppId(null); setPageId(null); setVerifyToken(null); inval(); },
    onError: (e) => toast.error(errText(e, "No se pudo guardar.")),
  });
  const action = useMutation({
    mutationFn: async (kind: "test" | "subscribe" | "sync") => {
      if (kind === "test") { const r = await api.post<{ name: string }>("/mkt/facebook/settings/test"); return `Conexión correcta: página "${r.name}".`; }
      if (kind === "subscribe") { await api.post("/mkt/facebook/settings/subscribe"); return "Página suscrita al webhook (leadgen)."; }
      const r = await api.post<{ total: number; created: number }>("/mkt/facebook/forms/sync");
      return `${r.total} formularios sincronizados (${r.created} nuevos).`;
    },
    onSuccess: (m) => { toast.success(m); inval(); },
    onError: (e) => toast.error(errText(e, "No se pudo completar la operación con Meta.")),
  });

  if (cfg.isLoading || !cfg.data) return <Card className="p-6"><Loading /></Card>;
  const c = cfg.data;
  const ready = Boolean(c.pageId && c.hasPageToken);

  return (
    <Card className="p-6 space-y-5">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Megaphone size={18} strokeWidth={1.75} className="text-primary" /> Facebook · Lead Ads
        </h3>
        <p className="text-sm text-ink-secondary mt-1">
          Recibe al instante los leads de los formularios de Facebook/Instagram, crea o actualiza el contacto (dedup por correo) y lanza las automatizaciones.
          Graph API {c.graphVersion}.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge color={c.hasAppSecret ? "green" : "gray"}>App Secret {c.hasAppSecret ? "guardado" : "sin guardar"}</Badge>
          <Badge color={c.hasPageToken ? "green" : "gray"}>Token de página {c.hasPageToken ? "guardado" : "sin guardar"}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="App ID"><Input value={appId ?? c.appId} onChange={(e) => setAppId(e.target.value)} inputMode="numeric" placeholder="123456789012345" /></Field>
        <Field label="App Secret" hint={c.hasAppSecret ? "Guardado. Escribe uno nuevo solo para reemplazarlo." : "Se usa para verificar la firma del webhook."}>
          <SecretInput value={appSecret} onChange={setAppSecret} placeholder={c.hasAppSecret ? "•••••••• (guardado)" : "App Secret de la app de Meta"} />
        </Field>
        <Field label="Page ID"><Input value={pageId ?? c.pageId} onChange={(e) => setPageId(e.target.value)} inputMode="numeric" placeholder="ID de la página de Facebook" /></Field>
        <Field label="Token de acceso de página (permanente)" hint={c.hasPageToken ? "Guardado. Escribe uno nuevo solo para reemplazarlo." : "Token del usuario del sistema con acceso a la página."}>
          <SecretInput value={pageToken} onChange={setPageToken} placeholder={c.hasPageToken ? "•••••••• (guardado)" : "EAAB…"} />
        </Field>
        <Field label="URL del webhook (pégala en Meta)">
          <div className="flex gap-2">
            <Input value={c.webhookUrl} readOnly onFocus={(e) => e.currentTarget.select()} />
            <Button onClick={() => copy(c.webhookUrl)} title="Copiar"><Copy size={15} strokeWidth={1.75} /></Button>
          </div>
        </Field>
        <Field label="Verify Token (pégalo en Meta)">
          <div className="flex gap-2">
            <Input value={verifyToken ?? c.verifyToken} onChange={(e) => setVerifyToken(e.target.value)} spellCheck={false} />
            <Button onClick={() => copy(verifyToken ?? c.verifyToken)} title="Copiar"><Copy size={15} strokeWidth={1.75} /></Button>
            <Button title="Generar otro" onClick={() => { if (window.confirm("¿Generar un Verify Token nuevo? Tendrás que actualizarlo en Meta.")) save.mutate({ regenerateVerifyToken: true }); }}>
              <RefreshCw size={15} strokeWidth={1.75} />
            </Button>
          </div>
        </Field>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => save.mutate({})} disabled={save.isPending}><Save size={16} strokeWidth={1.75} /> {save.isPending ? "Guardando…" : "Guardar"}</Button>
        <Button onClick={() => action.mutate("test")} disabled={!ready || action.isPending}><Send size={16} strokeWidth={1.75} /> Probar conexión</Button>
        <Button onClick={() => action.mutate("subscribe")} disabled={!ready || action.isPending}><Link2 size={16} strokeWidth={1.75} /> Suscribir página</Button>
        <Button onClick={() => action.mutate("sync")} disabled={!ready || action.isPending}><RefreshCw size={16} strokeWidth={1.75} /> Sincronizar formularios</Button>
        {c.hasPageToken && (
          <Button variant="danger" onClick={() => { if (window.confirm("¿Eliminar el token de página y el App Secret?")) save.mutate({ clearPageToken: true, clearAppSecret: true }); }}>
            <Trash2 size={16} strokeWidth={1.75} /> Quitar credenciales
          </Button>
        )}
      </div>

      <details className="rounded-md border border-line p-3 text-sm text-ink-secondary">
        <summary className="cursor-pointer font-medium text-ink">Pasos de configuración en Meta</summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1.5">
          <li>En <b>developers.facebook.com</b> crea una app de tipo <b>Empresa</b> vinculada al portafolio comercial (Business Manager) dueño de la página.</li>
          <li>Añade el producto <b>Webhooks</b> → objeto <b>Page</b> → “Suscribirse a este objeto” con la URL del webhook y el Verify Token de arriba; suscribe el campo <b>leadgen</b>.</li>
          <li>Permisos de la app: <b>leads_retrieval</b>, <b>pages_show_list</b>, <b>pages_read_engagement</b>, <b>pages_manage_metadata</b> (y <b>pages_manage_ads</b> si Meta lo pide).</li>
          <li>En Business Manager → Usuarios del sistema crea uno (admin), asígnale la página y la app, y genera un <b>token permanente</b> con esos permisos. Pega aquí el App ID, el App Secret (Configuración básica de la app), el Page ID y el token.</li>
          <li>Completa la <b>verificación del negocio</b> y la <b>App Review</b> de los permisos; pasa la app a modo <b>En vivo</b> (en modo desarrollo solo llegan leads de prueba).</li>
          <li>En la configuración de leads de la página (Herramientas de Lead Ads → CRM), da acceso a la app si aparece restringido.</li>
          <li>Pulsa <b>Probar conexión</b>, <b>Suscribir página</b> y <b>Sincronizar formularios</b>. Prueba con la herramienta <i>Lead Ads Testing Tool</i> de Meta.</li>
        </ol>
      </details>

      <FbForms forms={forms.data?.forms ?? []} loading={forms.isLoading} onChanged={inval} />
      <FbLeads />
    </Card>
  );
}

const TARGETS: Array<[string, string]> = [
  ["firstName", "Nombre"], ["lastName", "Apellido"], ["fullName", "Nombre completo"], ["email", "Email"], ["phone", "Teléfono"],
  ["company", "Empresa"], ["title", "Cargo"], ["city", "Ciudad"], ["country", "País"], ["ignore", "No importar"],
];
const STANDARD_FIELDS: Array<[string, string]> = [
  ["full_name", "fullName"], ["first_name", "firstName"], ["last_name", "lastName"], ["email", "email"], ["phone_number", "phone"],
  ["company_name", "company"], ["job_title", "title"], ["city", "city"], ["country", "country"],
];

function FbForms({ forms, loading, onChanged }: { forms: FbForm[]; loading: boolean; onChanged: () => void }): JSX.Element {
  const [open, setOpen] = useState<string | null>(null);
  if (loading) return <Loading />;
  return (
    <div>
      <h4 className="text-sm font-semibold text-ink mb-2">Formularios de Lead Ads</h4>
      {forms.length === 0 ? <p className="text-sm text-ink-muted">Aún no hay formularios. Usa “Sincronizar formularios”.</p> : (
        <div className="divide-y divide-line border border-line rounded-md">
          {forms.map((f) => (
            <div key={f.id} className="p-3">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-ink truncate">{f.name}</div>
                  <div className="text-xs text-ink-muted">
                    ID {f.fbFormId} · {f.settings.meta?.status ?? "—"} · {f.leadsCount} leads en Tiger{f.lastLeadAt ? ` · último ${formatDateTime(f.lastLeadAt)}` : ""}
                    {f.failedLeads > 0 && <span className="text-danger"> · {f.failedLeads} con error</span>}
                  </div>
                </div>
                <Button small onClick={() => setOpen(open === f.id ? null : f.id)}><Settings2 size={14} strokeWidth={1.75} /> Configurar</Button>
              </div>
              {open === f.id && <FbFormConfig form={f} onSaved={onChanged} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FbFormConfig({ form, onSaved }: { form: FbForm; onSaved: () => void }): JSX.Element {
  const lk = useQuery<Lk>({ queryKey: ["mkt-flow-lookups"], queryFn: () => api.get("/mkt/flows/lookups"), staleTime: 60_000 });
  const s = form.settings;
  const [tags, setTags] = useState((s.tags ?? []).join(", "));
  const [listIds, setListIds] = useState<string[]>(s.listIds ?? []);
  const [ownerId, setOwnerId] = useState(s.ownerId ?? "");
  const [fieldMap, setFieldMap] = useState<Record<string, string>>(s.fieldMap ?? {});
  const [days, setDays] = useState(7);

  const questions = (s.meta?.questions ?? []).filter((q) => q.key && !STANDARD_FIELDS.some(([k]) => k === q.key));
  const rows: Array<{ key: string; label: string; def: string }> = [
    ...STANDARD_FIELDS.map(([k, t]) => ({ key: k, label: k, def: t })),
    ...questions.map((q) => ({ key: q.key, label: q.label || q.key, def: "custom:fb_" + q.key.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 50) })),
  ];

  const save = useMutation({
    mutationFn: () => api.put(`/mkt/facebook/forms/${form.id}`, {
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 20), listIds, ownerId: ownerId || null, fieldMap,
    }),
    onSuccess: () => { toast.success("Formulario configurado."); onSaved(); },
    onError: (e) => toast.error(errText(e, "No se pudo guardar.")),
  });
  const imp = useMutation({
    mutationFn: () => api.post<{ fetched: number; processed: number; duplicates: number; failed: number }>(`/mkt/facebook/forms/${form.id}/import`, { days }),
    onSuccess: (r) => { toast.success(`${r.fetched} leads leídos: ${r.processed} nuevos, ${r.duplicates} ya existían, ${r.failed} con error.`); onSaved(); },
    onError: (e) => toast.error(errText(e, "No se pudieron importar los leads.")),
  });

  if (lk.isLoading || !lk.data) return <Loading />;
  return (
    <div className="mt-3 space-y-4 rounded-md bg-surface-hover/40 p-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Etiquetas (separadas por coma)"><Input value={tags} onChange={(e) => setTags(e.target.value)} /></Field>
        <Field label="Propietario de los contactos nuevos">
          <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Por defecto (administrador)</option>
            {lk.data.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Añadir a listas">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {lk.data.lists.length === 0 && <span className="text-sm text-ink-muted">No hay listas.</span>}
          {lk.data.lists.map((l) => (
            <label key={l.id} className="flex items-center gap-1.5 text-sm text-ink-secondary">
              <input type="checkbox" checked={listIds.includes(l.id)}
                onChange={(e) => setListIds((prev) => (e.target.checked ? [...prev, l.id] : prev.filter((x) => x !== l.id)))} />
              {l.name}
            </label>
          ))}
        </div>
      </Field>
      <div>
        <div className="label">Mapeo de campos</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {rows.map((r) => (
            <div key={r.key} className="flex items-center gap-2">
              <span className="text-sm text-ink-secondary w-40 shrink-0 truncate" title={r.key}>{r.label}</span>
              <Select value={fieldMap[r.key] ?? r.def} onChange={(e) => setFieldMap((m) => ({ ...m, [r.key]: e.target.value }))}>
                {TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                {r.def.startsWith("custom:") && <option value={r.def}>Dato personalizado ({r.def.slice(7)})</option>}
                {lk.data.customFields.map((cf) => <option key={cf.key} value={`custom:${cf.key}`}>Campo: {cf.label}</option>)}
              </Select>
            </div>
          ))}
        </div>
        <p className="text-xs text-ink-muted mt-1">Las preguntas personalizadas sin mapear se guardan en los datos personalizados del contacto (fb_…).</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}><Save size={15} strokeWidth={1.75} /> Guardar</Button>
        <span className="flex-1" />
        <Field label="Importar leads de los últimos">
          <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 7, 30, 90].map((d) => <option key={d} value={d}>{d} días</option>)}
          </Select>
        </Field>
        <Button onClick={() => imp.mutate()} disabled={imp.isPending}><Download size={15} strokeWidth={1.75} /> {imp.isPending ? "Importando…" : "Importar leads recientes"}</Button>
      </div>
    </div>
  );
}

function FbLeads(): JSX.Element {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const q = useQuery<{ leads: FbLead[]; total: number }>({ queryKey: ["fb-leads", status], queryFn: () => api.get(`/mkt/facebook/leads${status ? `?status=${status}` : ""}`) });
  const retry = useMutation({
    mutationFn: (id: string) => api.post<{ status: string; error?: string }>(`/mkt/facebook/leads/${id}/retry`),
    onSuccess: (r) => { if (r.status === "processed") toast.success("Lead procesado."); else toast.error(r.error ?? "Sigue fallando."); void qc.invalidateQueries({ queryKey: ["fb-leads"] }); },
    onError: (e) => toast.error(errText(e, "No se pudo reintentar.")),
  });
  const LBL: Record<string, [string, string]> = { processed: ["Procesado", "green"], failed: ["Fallido", "red"], received: ["Recibido", "blue"] };
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-semibold text-ink">Leads recibidos</h4>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="!w-40">
          <option value="">Todos</option><option value="processed">Procesados</option><option value="failed">Fallidos</option><option value="received">Recibidos</option>
        </Select>
      </div>
      {!q.data?.leads.length ? <p className="text-sm text-ink-muted">{q.isLoading ? "Cargando…" : "Sin leads todavía."}</p> : (
        <div className="overflow-x-auto border border-line rounded-md">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-ink-muted border-b border-line">
              <tr><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Lead</th><th className="px-3 py-2">Formulario</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2">Detalle</th><th /></tr>
            </thead>
            <tbody>
              {q.data.leads.map((l) => (
                <tr key={l.id} className="border-b border-line last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap text-ink-secondary">{formatDateTime(l.createdAt)}</td>
                  <td className="px-3 py-2">{l.contactId ? <a className="text-primary hover:underline" href={`/contacts/${l.contactId}`}>{l.leadgenId}</a> : l.leadgenId}</td>
                  <td className="px-3 py-2 text-ink-secondary">{l.fbFormId ?? "—"}</td>
                  <td className="px-3 py-2"><Badge color={LBL[l.status]?.[1] ?? "gray"}>{LBL[l.status]?.[0] ?? l.status}</Badge></td>
                  <td className="px-3 py-2 text-ink-secondary">{l.error ?? ""}</td>
                  <td className="px-3 py-2 text-right">{l.status === "failed" && <Button small onClick={() => retry.mutate(l.id)} disabled={retry.isPending}>Reintentar</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
